import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, AlertTriangle, Check, XCircle, RefreshCw,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 132 — Importación masiva de Gastos desde Excel.
 *
 * Flujo: Descargar plantilla → Rellenar → Subir → Vista previa (valida) →
 * Confirmar (aplica). El botón "Importar" queda deshabilitado durante la
 * petición para evitar dobles clics; el backend además usa `idempotency_key`
 * para bloquear reenvíos accidentales.
 */
export default function ExpenseBulkImportPanel({ onImported }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [applyResult, setApplyResult] = useState(null);
  // Filas marcadas como duplicadas que el usuario decide OMITIR.
  const [skipRowIndexes, setSkipRowIndexes] = useState([]);
  // Idempotency key generada UNA vez por preview.
  const idempotencyRef = useRef("");
  const inputRef = useRef(null);

  const reset = () => {
    setPreview(null);
    setApplyResult(null);
    setSkipRowIndexes([]);
    setFileName("");
    idempotencyRef.current = "";
    if (inputRef.current) inputRef.current.value = "";
  };

  const closeAll = () => {
    reset();
    setOpen(false);
  };

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      const r = await api.get("/expenses/bulk/template", { responseType: "blob" });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "plantilla_gastos.xlsx";
      a.click();
      toast.success("Plantilla descargada. Rellena los gastos y súbela.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setFileName(file.name);
    setApplyResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/expenses/bulk/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      // Nueva key para esta ronda de import.
      idempotencyRef.current = (crypto.randomUUID && crypto.randomUUID()) || `k-${Date.now()}-${Math.random()}`;
      toast.success("Vista previa lista. Revisa antes de importar.");
    } catch (err) {
      setPreview(null);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const duplicateRowIdx = useMemo(() => {
    return new Set((preview?.duplicates || []).map((d) => d.row_index));
  }, [preview]);

  const toggleSkip = (idx) => {
    setSkipRowIndexes((prev) => prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx]);
  };

  const skipAllDuplicates = () => {
    setSkipRowIndexes(Array.from(duplicateRowIdx));
  };

  const importAll = () => {
    setSkipRowIndexes([]);
  };

  const applyImport = async () => {
    if (!preview) return;
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/expenses/bulk/apply", {
        idempotency_key: idempotencyRef.current,
        rows: (preview.rows_valid || []).map((row) => ({
          row_index: row.row_index,
          expense_date: row.expense_date,
          supplier_code: row.supplier_code,
          supplier_name: row.supplier_name,
          supplier_tax_id: row.supplier_tax_id,
          invoice_number: row.invoice_number,
          description: row.description,
          category: row.category,
          base_amount: row.base_amount,
          vat_rate: row.vat_rate,
          total: row.total,
          payment_method: row.payment_method,
          status: row.status,
          internal_notes: row.internal_notes,
        })),
        accept_duplicates: false,
        skip_row_indexes: skipRowIndexes,
      });
      setApplyResult(r.data);
      const msg = r.data.idempotent_replay
        ? "Importación ya procesada anteriormente (idempotente)."
        : `Importados ${r.data.count_created} gastos, ${r.data.count_new_suppliers} proveedores nuevos.`;
      toast.success(msg);
      if (onImported) onImported();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-md bg-[#6E8B3D] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#5c7534] disabled:opacity-50"
        onClick={() => setOpen(true)}
        data-testid="expenses-import-btn"
      >
        <Upload size={14} /> Importar Excel
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="expenses-import-modal"
             onClick={(e) => e.target === e.currentTarget && closeAll()}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 bg-[#6E8B3D] text-white flex items-center justify-between">
              <div className="flex items-center gap-2 font-display font-black text-lg">
                <FileSpreadsheet size={18} /> IMPORTAR GASTOS DESDE EXCEL
              </div>
              <button onClick={closeAll} className="hover:bg-white/20 rounded p-1" data-testid="expenses-import-close">
                <XCircle size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {/* Paso 1: descargar plantilla */}
              <div className="border rounded-md p-4 bg-gray-50">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-md border bg-white px-3 py-1.5 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                    onClick={downloadTemplate}
                    disabled={busy}
                    data-testid="expenses-import-template-btn"
                  >
                    <Download size={14} /> Descargar plantilla
                  </button>
                  <span className="text-sm text-gray-600">
                    Rellena la plantilla y súbela. La hoja "Proveedores (ref)" contiene los IDs actuales.
                  </span>
                </div>
              </div>

              {/* Paso 2: subir archivo */}
              <div className="border rounded-md p-4">
                <label className="block text-sm font-semibold mb-2">Subir archivo relleno</label>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                  disabled={busy}
                  className="text-sm"
                  data-testid="expenses-import-file-input"
                />
                {fileName && <div className="text-xs text-gray-500 mt-1">Archivo: {fileName}</div>}
              </div>

              {/* Paso 3: vista previa */}
              {preview && (
                <div className="space-y-3" data-testid="expenses-import-preview">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <Card label="Filas válidas" val={preview.totals.count_valid} ok />
                    <Card label="Filas con error" val={preview.totals.count_invalid} bad={preview.totals.count_invalid > 0} />
                    <Card label="Proveedores nuevos" val={preview.totals.count_new_suppliers} warn={preview.totals.count_new_suppliers > 0} />
                    <Card label="Posibles duplicados" val={preview.totals.count_duplicates} warn={preview.totals.count_duplicates > 0} />
                    <Card label="Base imponible" val={`€ ${preview.totals.base.toFixed(2)}`} />
                    <Card label="IVA" val={`€ ${preview.totals.vat.toFixed(2)}`} />
                    <Card label="Total" val={`€ ${preview.totals.total.toFixed(2)}`} strong />
                  </div>

                  {preview.rows_invalid?.length > 0 && (
                    <div className="border border-red-300 bg-red-50 rounded-md p-3">
                      <div className="font-semibold text-red-800 flex items-center gap-2 mb-1">
                        <AlertTriangle size={16} /> Filas con errores ({preview.rows_invalid.length})
                      </div>
                      <ul className="text-xs text-red-700 list-disc pl-4 space-y-0.5" data-testid="expenses-import-errors-list">
                        {preview.rows_invalid.map((r) => (
                          <li key={r.row_index}>Fila {r.row_index}: {r.errors.join("; ")}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {preview.suppliers_new_to_create?.length > 0 && (
                    <div className="border border-amber-300 bg-amber-50 rounded-md p-3">
                      <div className="font-semibold text-amber-900 mb-1">Proveedores nuevos que se crearán ({preview.suppliers_new_to_create.length})</div>
                      <ul className="text-xs text-amber-900 space-y-0.5" data-testid="expenses-import-new-suppliers-list">
                        {preview.suppliers_new_to_create.map((s) => (
                          <li key={s.dedup_key}>
                            <strong>{s.name}</strong>{s.tax_id ? ` — ${s.tax_id}` : ""} (filas: {s.rows.join(", ")})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {preview.duplicates?.length > 0 && (
                    <div className="border border-orange-300 bg-orange-50 rounded-md p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-orange-900 flex items-center gap-2">
                          <AlertTriangle size={16} /> Posibles duplicados ({preview.duplicates.length})
                        </div>
                        <div className="flex gap-2">
                          <button className="text-xs underline" onClick={skipAllDuplicates} data-testid="expenses-import-skip-all-dups">Omitir todos</button>
                          <button className="text-xs underline" onClick={importAll} data-testid="expenses-import-keep-all-dups">Importar igualmente</button>
                        </div>
                      </div>
                      <ul className="text-xs text-orange-900 space-y-1" data-testid="expenses-import-duplicates-list">
                        {preview.duplicates.map((d, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={skipRowIndexes.includes(d.row_index)}
                              onChange={() => toggleSkip(d.row_index)}
                              data-testid={`expenses-import-skip-${d.row_index}`}
                            />
                            Fila {d.row_index}: Nº {d.invoice_number} — {d.reason === "existe_en_bd"
                              ? `ya existe (${d.existing_date}, €${(d.existing_total || 0).toFixed(2)})`
                              : "duplicado dentro del lote"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {applyResult && (
                    <div className="border border-green-300 bg-green-50 rounded-md p-3 text-sm text-green-900" data-testid="expenses-import-result">
                      <div className="font-semibold flex items-center gap-2"><Check size={16} /> Importación completada</div>
                      <div>Gastos creados: <strong>{applyResult.count_created}</strong></div>
                      <div>Proveedores nuevos: <strong>{applyResult.count_new_suppliers}</strong></div>
                      {applyResult.count_errors > 0 && (
                        <div className="text-red-700">Errores: {applyResult.count_errors}</div>
                      )}
                      {applyResult.count_skipped > 0 && (
                        <div>Omitidos por usuario: {applyResult.count_skipped}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50">
              <button onClick={reset} className="px-3 py-1.5 text-sm border rounded-md hover:bg-white" disabled={busy}>
                <RefreshCw size={14} className="inline mr-1" /> Reiniciar
              </button>
              <button onClick={closeAll} className="px-3 py-1.5 text-sm border rounded-md hover:bg-white" disabled={busy}>
                Cerrar
              </button>
              <button
                onClick={applyImport}
                disabled={busy || !preview || preview.totals.count_valid === 0 || !!applyResult}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-md bg-[#6E8B3D] text-white hover:bg-[#5c7534] disabled:opacity-50"
                data-testid="expenses-import-apply-btn"
              >
                <Check size={14} /> Confirmar e importar {preview ? `(${(preview.totals.count_valid || 0) - skipRowIndexes.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Card({ label, val, ok = false, bad = false, warn = false, strong = false }) {
  const cls = bad ? "border-red-300 bg-red-50" : warn ? "border-amber-300 bg-amber-50" : ok ? "border-green-300 bg-green-50" : "border-gray-200 bg-white";
  return (
    <div className={`border rounded-md p-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-lg ${strong ? "font-black" : "font-semibold"}`}>{val}</div>
    </div>
  );
}
